/**
 * Analytics Settings tab — manage accounts excluded from platform analytics.
 *
 * Two scopes (migration 1513):
 *   • 'all'          — excluded from every analytics surface. Test/dev accounts.
 *   • 'revenue_only' — counted in engagement (DAU/sessions/etc.) but NOT in
 *                      revenue (MRR/ARR/paying users). Beta family.
 *
 * Displays:
 *   • A note that all users with user_class = 'unlimited_tester' are excluded.
 *   • The manually-managed email list with add / scope-flip / remove controls.
 *   • A note that legitimate RevenueCat subscribers are always included even
 *     if their email is in the list (applies to scope='all' only — for
 *     scope='revenue_only' the paying status is deliberately bypassed).
 *   • A note explaining the 'revenue_only' scope (beta family).
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff, Trash2, UserX, Plus, Info, Users } from 'lucide-react';
import { adminFetch, adminMutate } from '../../../../utils/adminApi';
import { cn } from '../../../../lib/cn';

type ExclusionScope = 'all' | 'revenue_only';

interface Exclusion {
  id: number;
  email: string;
  notes: string | null;
  added_by: string | null;
  added_at: string | null;
  scope: ExclusionScope;
}

interface ExclusionsPayload {
  exclusions: Exclusion[];
  unlimited_tester_count: number;
  active_subscriber_overrides: number;
  revenue_only_count: number;
}

interface ExclusionsResponse {
  success: boolean;
  data: ExclusionsPayload;
}

export const SettingsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newScope, setNewScope] = useState<ExclusionScope>('all');
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<ExclusionsResponse>({
    queryKey: ['analytics-exclusions'],
    queryFn: () => adminFetch<ExclusionsResponse>('/api/admin/analytics/settings/exclusions'),
    staleTime: 60 * 1000,
  });

  const addMutation = useMutation({
    mutationFn: ({ email, notes, scope }: { email: string; notes: string; scope: ExclusionScope }) =>
      adminMutate('/api/admin/analytics/settings/exclusions', 'POST', {
        email,
        notes: notes || null,
        scope,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-exclusions'] });
      setNewEmail('');
      setNewNotes('');
      setNewScope('all');
      setFormError(null);
    },
    onError: (err: Error) => {
      setFormError(err.message ?? 'Failed to add exclusion.');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) =>
      adminMutate(`/api/admin/analytics/settings/exclusions/${id}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-exclusions'] });
    },
  });

  // PATCH to flip an entry's scope between 'all' and 'revenue_only' in place
  // (no delete-and-re-add cycle). Hits routers/admin_analytics_v2/settings.py:update_exclusion.
  const scopeMutation = useMutation({
    mutationFn: ({ id, scope }: { id: number; scope: ExclusionScope }) =>
      adminMutate(`/api/admin/analytics/settings/exclusions/${id}`, 'PATCH', { scope }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-exclusions'] });
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setFormError('Enter a valid email address.');
      return;
    }
    setFormError(null);
    addMutation.mutate({ email: trimmed, notes: newNotes.trim(), scope: newScope });
  };

  const payload = data?.data;

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Page description */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Analytics Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Accounts listed here are excluded from all platform analytics rollups — DAU, WAU,
          MAU, retention, revenue, relationship scores, and funnel data. Changes take effect
          on the next hourly rollup run.
        </p>
      </div>

      {/* Unlimited tester notice */}
      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <UserX className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Unlimited tester accounts are always excluded
            </p>
            <p className="mt-1 text-sm text-amber-700">
              Any user with account type <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-xs">unlimited_tester</code> is
              automatically excluded from analytics, regardless of whether their email appears
              in the list below.
              {payload != null && (
                <span className="ml-1 font-medium">
                  ({payload.unlimited_tester_count} account{payload.unlimited_tester_count !== 1 ? 's' : ''} currently)
                </span>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Subscriber override notice */}
      <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden />
          <div>
            <p className="text-sm font-medium text-blue-800">
              Legitimate subscribers are always included (scope = "all" only)
            </p>
            <p className="mt-1 text-sm text-blue-700">
              If a <strong>scope = "all"</strong> account upgrades to a paid plan and has an
              active RevenueCat subscription, their analytics data is counted regardless of
              whether their email is listed below. This guard does <strong>not</strong> apply
              to <strong>scope = "revenue_only"</strong> entries — those are deliberately
              excluded from revenue analytics even though they're paying.
              {payload != null && payload.active_subscriber_overrides > 0 && (
                <span className="ml-1 font-semibold text-blue-800">
                  {payload.active_subscriber_overrides} listed email{payload.active_subscriber_overrides !== 1 ? 's are' : ' is'} currently overridden this way.
                </span>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Revenue-only scope notice — beta family auto-detection */}
      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-start gap-3">
          <Users className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" aria-hidden />
          <div>
            <p className="text-sm font-medium text-emerald-800">
              Beta family — engagement YES, revenue NO (auto-detected)
            </p>
            <p className="mt-1 text-sm text-emerald-700">
              Users whose access was admin-granted (i.e. <code className="rounded bg-emerald-100 px-1 py-0.5 font-mono text-xs">admin_tier_override</code> is
              set, or <code className="rounded bg-emerald-100 px-1 py-0.5 font-mono text-xs">user_class</code> is{' '}
              <code className="rounded bg-emerald-100 px-1 py-0.5 font-mono text-xs">beta_tester</code>{' '}
              / <code className="rounded bg-emerald-100 px-1 py-0.5 font-mono text-xs">unlimited_tester</code>) are
              automatically counted in engagement metrics but excluded from revenue —
              their usage is real, their revenue contribution is comp'd. No seeding required;
              the rule reads from user state at query time.
            </p>
            <p className="mt-1 text-sm text-emerald-700">
              Use the scope <code className="rounded bg-emerald-100 px-1 py-0.5 font-mono text-xs">revenue_only</code> below
              for ad-hoc manual additions (users not auto-detected by the rule above).
              {payload != null && (
                <span className="ml-1 font-semibold text-emerald-800">
                  {payload.revenue_only_count} manual revenue_only entr{payload.revenue_only_count !== 1 ? 'ies' : 'y'} configured.
                </span>
              )}
              {' '}The Revenue tab has an "Include beta" toggle to override per-view if you
              want to see what the numbers look like with beta family counted.
            </p>
          </div>
        </div>
      </section>

      {/* Exclusion list */}
      <section>
        <h3 className="mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Excluded accounts
        </h3>

        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600">Failed to load exclusion list.</p>
        )}

        {!isLoading && payload && (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            {payload.exclusions.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-400">
                No accounts in the exclusion list.
              </p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Email</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Scope</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Notes</th>
                    <th className="px-4 py-2.5 text-left font-medium text-gray-600">Added by</th>
                    <th className="w-12 px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {payload.exclusions.map((ex) => (
                    <tr key={ex.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-800">{ex.email}</td>
                      <td className="px-4 py-2.5">
                        {/* Inline select to flip scope in place. PATCH endpoint
                            (see settings.py:update_exclusion) audit-logs the change. */}
                        <select
                          value={ex.scope}
                          onChange={(e) =>
                            scopeMutation.mutate({ id: ex.id, scope: e.target.value as ExclusionScope })
                          }
                          disabled={scopeMutation.isPending}
                          className={cn(
                            'rounded border border-gray-200 px-2 py-1 text-xs font-medium',
                            ex.scope === 'revenue_only'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-gray-50 text-gray-700',
                            'disabled:opacity-50',
                          )}
                        >
                          <option value="all">all (full exclusion)</option>
                          <option value="revenue_only">revenue_only (beta family)</option>
                        </select>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{ex.notes ?? '—'}</td>
                      <td className="px-4 py-2.5 text-gray-400 text-xs">{ex.added_by ?? '—'}</td>
                      <td className="px-4 py-2.5">
                        <button
                          type="button"
                          onClick={() => removeMutation.mutate(ex.id)}
                          disabled={removeMutation.isPending}
                          className={cn(
                            'flex items-center justify-center rounded p-1 transition-colors',
                            'text-gray-400 hover:bg-red-50 hover:text-red-600',
                            'disabled:opacity-40',
                          )}
                          title="Remove from exclusion list"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                          <span className="sr-only">Remove {ex.email}</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* Add form */}
      <section>
        <h3 className="mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Add account
        </h3>
        <form onSubmit={handleAdd} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor="exclude-email" className="mb-1 block text-xs font-medium text-gray-600">
              Email address
            </label>
            <input
              id="exclude-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@example.com"
              required
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm shadow-sm',
                'border-gray-300 placeholder-gray-400',
                'focus:border-coach-cedar focus:outline-none focus:ring-1 focus:ring-coach-cedar',
              )}
            />
          </div>
          <div className="w-full sm:w-56">
            <label htmlFor="exclude-scope" className="mb-1 block text-xs font-medium text-gray-600">
              Scope
            </label>
            <select
              id="exclude-scope"
              value={newScope}
              onChange={(e) => setNewScope(e.target.value as ExclusionScope)}
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm shadow-sm',
                'border-gray-300 bg-white',
                'focus:border-coach-cedar focus:outline-none focus:ring-1 focus:ring-coach-cedar',
              )}
            >
              <option value="all">all — full exclusion</option>
              <option value="revenue_only">revenue_only — beta family</option>
            </select>
          </div>
          <div className="flex-1">
            <label htmlFor="exclude-notes" className="mb-1 block text-xs font-medium text-gray-600">
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="exclude-notes"
              type="text"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              placeholder="e.g. Internal test account"
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm shadow-sm',
                'border-gray-300 placeholder-gray-400',
                'focus:border-coach-cedar focus:outline-none focus:ring-1 focus:ring-coach-cedar',
              )}
            />
          </div>
          <button
            type="submit"
            disabled={addMutation.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold',
              'bg-coach-cedar text-white shadow-sm transition-opacity',
              'hover:opacity-90 disabled:opacity-50',
              'sm:self-end',
            )}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {addMutation.isPending ? 'Adding…' : 'Add'}
          </button>
        </form>
        {formError && (
          <p className="mt-2 text-xs text-red-600">{formError}</p>
        )}
      </section>

      {/* Footer note */}
      <p className="flex items-center gap-1.5 text-xs text-gray-400">
        <ShieldOff className="h-3.5 w-3.5" aria-hidden />
        Exclusions apply at rollup time. Historical platform-level aggregates (DAU, MAU) for
        dates already rolled up will be corrected on the next nightly pass or via a manual
        backfill.
      </p>
    </div>
  );
};
